/* eslint-disable import/no-cycle */
/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dropdown,
  Menu,
  message as antMessage,
  Popconfirm,
  Skeleton,
  Form,
  Tooltip,
  Space
} from 'antd';
import {
  CheckOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  EllipsisOutlined,
  InfoCircleOutlined,
  MailOutlined,
  PrinterOutlined,
  FormOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  WarningOutlined,
  ContactsOutlined
} from '@ant-design/icons';
import { Link, useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';
import {
  routes,
  subRoutes,
  outOfNavRoutes,
  routesSiderRight
} from '../../utils/constants/routes';
import useErrorMessage from '../../utils/ErrorMessage';
import useAuthContext from '../../contexts/AuthContext';
import useQuotesContext from '../quotes/QuotesContext';
import ValidationQuoteModal from '../quotes/ValidationQuoteModal';
import { AnalyzeModal } from '../quotes/AnalyzeModal';
import PrintSignModal from './PrintSignModal';
import MultiplePrintModal from './MultiplePrintModal';
import MultipleSignModal from './MultipleSignModal';
import TextQuoteModal from './TextQuoteModal';
import useContractsContext from './ContractsContext';
import { FuturTerminationNotice } from './FuturTerminationNotice';

const Actions = ({
  isLoading,
  contract,
  shownContract,
  openLastStepModal,
  setOpenLastStepModal
}) => {
  const { message } = useErrorMessage();
  const { t } = useTranslation();
  const {
    setUpdateStatusVisible,
    setUpdateDetailsVisible,
    forceRefresh,
    setForceRefresh
  } = useContractsContext();
  const { dispatchAPI, user } = useAuthContext();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [isSignSending, setIsSignSending] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const { handleOpenModal } = useQuotesContext();
  const [openPrintModal, setOpenPrintModal] = useState(false);
  const [openSignModal, setOpenSignModal] = useState(false);
  const [openTextQuoteModal, setOpenTextQuoteModal] = useState(false);
  const [email, setEmail] = useState();
  const [action, setAction] = useState('');
  const [form] = Form.useForm();
  const [reclamations, setReclamations] = useState();
  const [futurTerminationNoticeVisible, setFuturTerminationNoticeVisible] =
    useState(false);
  const paperworkFeeNotPaid =
    contract?.paperwork_fee_invoice &&
    contract.paperwork_fee_invoice[0]?.paperworkFee_status === 'NOT_PAID' &&
    contract.paperwork_fee_invoice[0]?.paperwork_fee_payment_choices === 'CB';

  const ticketStatuses = [
    'IN_PROGRESS',
    'PENDING_COMPANY',
    'PENDING_REFERENT',
    'AWAITING_INFORMATION',
    'REFUSED',
    'WITHOUT_EFFECT'
  ];

  const terminationReason = [
    'TERMINATED_CLASSIC',
    'TERMINATED_WITHDRAWAL',
    'TERMINATED_AMICABLE',
    'TERMINATION_WITHOUT_EFFECT',
    'TERMINATION_NO_PAYMENT'
  ];

  const contractWithSupplier = ['MRPWK', 'MRPCM', 'MRPML', 'RCPWK', 'RCPML'];

  const disabledButton = () => {
    if (
      [
        'ACTIVE',
        'UNPAID_1',
        'UNPAID_2',
        'UNPAID_3',
        'FORMAL_NOTICE',
        'WARRANTY_SUSPENSION',
        'ACTIVE_AFTER_SUSPENSION',
        'PENDING'
      ].includes(contract?.status)
    ) {
      if (moment().isAfter(moment(contract?.effective_date).add(10, 'days'))) {
        if (
          contract?.documents
            ?.filter((i) => i.status !== 'DIVERS')
            .find((f) => f.status !== 'VALID' && f.required === true)
        ) {
          return true;
        }
      }
    }
    return false;
  };

  const getPreviousContractId = () => {
    if (contract?.amendment.length) {
      const lastActiveAmendments = [];
      contract.amendment
        .filter((f) => f.status === 'ACTIVE' || f.status === 'PENDING')
        .map((a) => {
          lastActiveAmendments.push(a._id);
          return true;
        });
      if (lastActiveAmendments.length) {
        return lastActiveAmendments[lastActiveAmendments.length - 1];
      }
      return contract?._id;
    }
    return contract?._id;
  };
  const getDocToPrint = (origin) => {
    if (
      form.getFieldValue('documents') &&
      form.getFieldValue('documents').startsWith('amendment')
    )
      return 'amendment';
    if (form.getFieldValue('documents')) return form.getFieldValue('documents');
    if (form.getFieldValue('quittancesPaid_date'))
      return form.getFieldValue('quittancesPaid_date');
    if (origin === 'signPrintContract') return 'contract';
    if (origin === 'signPrintAmendment') return 'amendment';
    return 'quote';
  };

  // Function to check in PrintSignModal if is for amendment or rootContract
  const getContractId = (lastAmendmentId, index) => {
    let result;
    if (getDocToPrint(origin) === 'amendment') {
      result = contract?.amendment[index]._id;
    } else {
      result = contract?._id;
    }
    if (lastAmendmentId) {
      result = lastAmendmentId;
    }
    return result;
  };

  // function to return the index value of amendment
  const index = () => {
    return form.getFieldValue('documents') &&
      form.getFieldValue('documents').startsWith('amendment')
      ? parseInt(form.getFieldValue('documents').split('_')[1], 10)
      : 0;
  };

  const signContract = async (origin, lastAmendmentId, fromSignModal) => {
    if (fromSignModal) {
      setOpenSignModal(false);
      setIsSignSending(true);
    } else {
      setDownloading(true);
    }
    try {
      let mailToRegister;
      if (!contract?.customer.email) {
        form.getFieldsError().some((item) => {
          if (item.errors.length !== 0) {
            return antMessage.error(t('contracts.modal.email_error'));
          }
          mailToRegister = form.getFieldValue('email');
          return true;
        });
      }
      if (mailToRegister || contract?.customer.email) {
        const { data } = await dispatchAPI('POST', {
          url: `/contracts/signContract`,
          body: {
            document: lastAmendmentId
              ? 'amendment'
              : await getDocToPrint(origin),
            email: mailToRegister,
            contractId: getContractId(lastAmendmentId, index())
          }
        });
        if (data) {
          setOpenLastStepModal(false);
          navigate(
            `${routes.CONTRACTS}${subRoutes.CONTRACTS.PORTFOLIO_CONTRACTS}/show/${contract?._id}`
          );
          antMessage.success(t('contracts.modal.email_send_success'));
        }
      }
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsSignSending(false);
    setDownloading(false);
    setForceRefresh(!forceRefresh);
  };

  const printContract = async (origin, lastAmendmentId) => {
    setIsDownloading(true);
    try {
      const { data, headers } = await dispatchAPI('GET', {
        url: `/contracts/download/${getContractId(
          lastAmendmentId,
          index()
        )}?documents=${
          lastAmendmentId ? 'amendment' : await getDocToPrint(origin)
        }&multiple=${
          form.getFieldValue('multipleDocuments')
            ? form.getFieldValue('multipleDocuments')
            : false
        }&year=${form.getFieldValue('quittancesPaid_date') || moment().year()}`,
        responseType: 'blob'
      });
      let fileName = headers['content-disposition'].substring(
        headers['content-disposition'].indexOf('=') + 1,
        headers['content-disposition'].length
      );
      const invoiceDate = form.getFieldValue('quittancesPaid_date');
      if (invoiceDate) {
        const invoiceTitle = fileName.split(' ');
        fileName = `${invoiceTitle[0]} ${invoiceDate} ${invoiceTitle[1]}`;
      }
      const blob = new Blob([data], {
        type: 'application/pdf'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}`;
      a.click();
      antMessage.success(t('export.messages.success'));
      form.resetFields();
      if (origin === 'signPrintAmendment' || origin === 'signPrintContract') {
        setOpenLastStepModal(false);
        navigate(
          `${routes.CONTRACTS}${subRoutes.CONTRACTS.PORTFOLIO_CONTRACTS}/show/${contract?._id}`
        );
      } else {
        setForceRefresh(!forceRefresh);
      }
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsDownloading(false);
  };
  const isReductScreen = useMediaQuery({ query: '(max-width: 1600px)' });

  const type = contract?.type?.toLowerCase();

  const sendContract = async (content, lastAmendmentId) => {
    setDownloading(true);
    try {
      await dispatchAPI('POST', {
        url: `/contracts/send/${getContractId(
          lastAmendmentId,
          index()
        )}?documents=${
          form.getFieldValue('documents')
            ? await getDocToPrint(origin)
            : 'quote'
        }&multiple=${
          form.getFieldValue('multipleDocuments')
            ? form.getFieldValue('multipleDocuments')
            : false
        }`,
        body: content
      });
      antMessage.success(t('export.messages.mail_success'));
      form.resetFields();
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setDownloading(false);
  };

  const deleteQuote = async () => {
    try {
      await dispatchAPI('DELETE', { url: `/contracts/${contract?._id}` });
      navigate(-1);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const getMRPParam = () => {
    const suffix = `${
      contractWithSupplier.includes(contract?.type)
        ? `&sup=${contract?.type.slice(-2).toLowerCase()}`
        : ''
    }`;
    if (contract?.activities[0] === 'Activité de bureau') {
      return `?mrpT=office${suffix}`;
    }
    return `?mrpT=any${suffix}`;
  };

  const getRCPParam = () => {
    return contractWithSupplier.includes(contract?.type)
      ? `?sup=${contract?.type.slice(-2).toLowerCase()}`
      : '';
  };

  const getAssoParam = () => {
    if (contract?.local) return '?assoT=localRc';
    return '?assoT=rc';
  };

  const getReclamation = async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/reclamations/contract/${id}`
      });
      setReclamations(data);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  useEffect(() => {
    if (id) {
      getReclamation();
    }
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (
      contract.status === 'QUOTE' &&
      contract.type === 'RCNGAWK' &&
      contract.quittances.length > 0 &&
      contract.quittances.some(
        (quittance) => quittance.quittance_status === 'PAID'
      )
    ) {
      handleOpenModal(contract?._id, contract?.type, contract);
    }
  }, [contract]);

  const menu = (
    <Menu>
      {![
        'QUOTE',
        'TERMINATED',
        'TERMINATED_NON_PAYMENT',
        'WITHOUT_EFFECT'
      ].includes(contract?.status) &&
        !paperworkFeeNotPaid && (
          <>
            <Menu.Item>
              <Button
                type="link"
                style={{ display: 'flex', justifyContent: 'start' }}
                onClick={() => {
                  setUpdateDetailsVisible(true);
                }}
                disabled={contract?.status === 'DELETE'}
              >
                <InfoCircleOutlined />
                {`${t('buttons.update_details')} `}
              </Button>
            </Menu.Item>
            <Menu.Divider />
          </>
        )}
      {contract?.status === 'QUOTE' && (
        <>
          <Menu.Item>
            <Button
              type="link"
              style={{ display: 'flex', justifyContent: 'start' }}
              onClick={() =>
                navigate(
                  `${routes.QUOTES}/${
                    (type.includes('wk') && type !== 'mriwk') ||
                    type.includes('cm') ||
                    (type.includes('ml') && ['rcpml', 'mrpml'].includes(type))
                      ? contract?.type.substring(0, 3).toLowerCase()
                      : type
                  }/edit/${contract?._id}/${
                    contract?.type?.includes('MRP') ? getMRPParam() : ''
                  }${contract?.type === 'ASSOCIATION' ? getAssoParam() : ''}${
                    contract?.type?.includes('RCP') ? getRCPParam() : ''
                  }`
                )
              }
            >
              <EditOutlined />
              {`${t('buttons.edit')} `}
            </Button>
          </Menu.Item>
          <Menu.Divider />
        </>
      )}
      {/* {!paperworkFeeNotPaid && contract?.type !== 'RCNGAWK' && (
        <Menu.Item>
          <Button
            type="link"
            onClick={() =>
              navigate(
                `${routes.QUOTES}/${
                  (type.includes('wk') && type !== 'mriwk') ||
                  type.includes('cm') ||
                  (type.includes('ml') && ['rcpml', 'mrpml'].includes(type))
                    ? contract?.type.substring(0, 3).toLowerCase()
                    : type
                }/duplicate/${getPreviousContractId()}${
                  contract?.type === 'ASSOCIATION'
                    ? getAssoParam()
                    : contract?.type === 'MRPWK' ||
                      contract?.type === 'MRPCM' ||
                      contract?.type === 'MRPML'
                    ? getMRPParam()
                    : ['RCPWK', 'RCPCM', 'RCPML'].includes(contract?.type)
                    ? getRCPParam()
                    : ''
                }`
              )
            }
          >
            <CopyOutlined />
            {t('buttons.duplicate')}
          </Button>
        </Menu.Item>
      )} */}
      {contract?.status === 'QUOTE' && (
        <>
          <Menu.Divider />
          <Menu.Item>
            <Popconfirm
              title={t('datatable.column.action.delete.title')}
              okText={t('datatable.column.action.delete.ok')}
              okButtonProps={{ danger: true }}
              cancelText={t('datatable.column.action.delete.cancel')}
              onConfirm={deleteQuote}
              icon={<WarningOutlined />}
            >
              <Button
                type="link"
                style={{ display: 'flex', justifyContent: 'center' }}
              >
                <DeleteOutlined />
                {`${t('buttons.delete')} `}
              </Button>
            </Popconfirm>
          </Menu.Item>
        </>
      )}
      {![
        'QUOTE',
        'TERMINATED',
        'TERMINATED_NON_PAYMENT',
        'WITHOUT_EFFECT'
      ].includes(contract?.status) &&
        contract?.type !== 'RCNGAWK' &&
        !terminationReason.includes(contract?.termination_reason) && (
          <>
            <Menu.Divider />
            <Menu.Item>
              <Tooltip
                title={
                  disabledButton()
                    ? 'Actions limitées : la date d’effet a plus de 10 jours et des documents sont non conformes ou manquants dans la GED.'
                    : undefined
                }
                placement="left"
              >
                <Button
                  type="link"
                  style={{ display: 'flex', justifyContent: 'center' }}
                  onClick={() => setUpdateStatusVisible(true)}
                  disabled={contract?.status === 'DELETE' || disabledButton()}
                >
                  <StopOutlined />
                  {`${t('buttons.terminate')} `}
                </Button>
              </Tooltip>
            </Menu.Item>
          </>
        )}
    </Menu>
  );

  const getEditButtonLabel = () => {
    if (contract?.previous_contract) return 'amendment';
    if (contract?.status === 'QUOTE') return 'quote';
    return 'contract';
  };

  const checkMissingParts = (documents, name, amendmentNumber) => {
    let result;
    if (!amendmentNumber) {
      // eslint-disable-next-line array-callback-return
      documents.map((document) => {
        if (document.name === name) {
          if (document.status === 'MISSING') {
            result = true;
          }
        }
      });
    } else {
      // eslint-disable-next-line array-callback-return
      documents.map((document) => {
        if (document.name === name) {
          if (document.amendmentNumber === parseInt(amendmentNumber, 10)) {
            if (document.status === 'MISSING') {
              result = true;
            } else {
              result = false;
            }
          }
        }
      });
    }
    return result;
  };

  const checkContractToSign = () => {
    let result;
    // if contract is not signed
    // and status is acive
    // and contractSign document is not saved, sent or valid
    if (
      !contract?.contract_signed &&
      contract?.status === 'ACTIVE' &&
      checkMissingParts(contract.documents, 'contractSign')
    ) {
      result = true;
    } else {
      result = false;
    }
    // eslint-disable-next-line array-callback-return
    (contract?.amendment || []).map((a) => {
      // if an amendment is not signed with an active status
      if (
        !a.contract_signed &&
        a.status === 'ACTIVE' &&
        checkMissingParts(
          contract.documents,
          'amendmentSign',
          a.number.split('_AV')[1]
        )
      ) {
        result = true;
      } else {
        result = false;
      }
    });

    return result;
  };

  return (
    <Skeleton loading={isLoading} paragraph={false}>
      <FuturTerminationNotice
        open={futurTerminationNoticeVisible}
        close={() => setFuturTerminationNoticeVisible(false)}
      />
      <>
        <AnalyzeModal
          tracfinOverride={contract.exemption?.exemptions?.includes('tracfin')}
        />
        <ValidationQuoteModal />
        <PrintSignModal
          openLastStepModal={openLastStepModal}
          printContract={printContract}
          signContract={signContract}
          isDownloading={isDownloading}
          downloading={downloading}
          contract={contract}
          setEmail={setEmail}
          email={email}
          form={form}
        />
        <Space>
          <>
            {contract?.status === 'quote' && !contract?.exemption && (
              <Link
                to={{
                  pathname: `${routes.QUOTES}/${
                    (type.includes('wk') && type !== 'mriwk') ||
                    type.includes('cm') ||
                    (type.includes('ml') && ['rcpml', 'mrpml'].includes(type))
                      ? contract?.type.substring(0, 3).toLowerCase()
                      : type
                  }/${
                    contract?.previous_contract ? 'edit_amendment' : 'edit'
                  }/${contract?._id}`
                }}
              >
                <Button
                  type="primary"
                  style={{
                    width: isReductScreen ? 35 : 'auto',
                    padding: isReductScreen ? '4px 4px' : '4px 15px'
                  }}
                >
                  <span style={{ display: isReductScreen ? 'none' : 'inline' }}>
                    {t(`buttons.edit_${getEditButtonLabel()}`)}
                  </span>
                  <EditOutlined
                    style={{
                      marginLeft: isReductScreen ? 0 : 8
                    }}
                  />
                </Button>
              </Link>
            )}
            {user?.role === 'users:SUPER-USER' && !!reclamations?.length && (
              <Link
                to={`${outOfNavRoutes.SETTINGS}${routesSiderRight.BROKER_CLAIMS}?f=contratId=${id}__`}
              >
                <Button
                  type={
                    reclamations?.filter((r) => r.status === 'WAITING').length >
                    0
                      ? 'danger'
                      : 'primary'
                  }
                  style={{
                    width: isReductScreen ? 35 : 'auto',
                    padding: isReductScreen ? '4px 4px' : '4px 15px'
                  }}
                >
                  <span
                    style={{
                      display: isReductScreen ? 'none' : 'inline'
                    }}
                  >
                    {reclamations?.filter((r) => r.status === 'WAITING')
                      .length > 0
                      ? `${
                          reclamations?.filter((r) => r.status === 'WAITING')
                            .length
                        } ${t('contracts.form.title.action.reclamations')} ${t(
                          'contracts.form.title.action.waiting'
                        )}`
                      : `${reclamations?.length} ${t(
                          'contracts.form.title.action.reclamations'
                        )}`}
                  </span>
                  <ExclamationCircleOutlined
                    style={{
                      marginLeft: isReductScreen ? 0 : 8
                    }}
                  />
                </Button>
              </Link>
            )}
            {!ticketStatuses.includes(contract?.exemption?.status) &&
              contract?.status !== 'QUOTE' &&
              checkContractToSign() && (
                <Button
                  onClick={() => {
                    if (contract?.status !== 'QUOTE') {
                      setOpenSignModal(true);
                    }
                    setAction('sign');
                  }}
                  type="danger"
                  style={{
                    width: isReductScreen ? 35 : 'auto',
                    padding: isReductScreen ? '4px 4px' : '4px 15px'
                  }}
                  loading={isSignSending}
                  disabled={contract?.status === 'DELETE'} // add disabled if quote
                >
                  <span
                    style={{
                      display: isReductScreen ? 'none' : 'inline'
                    }}
                  >
                    {t('contracts.form.title.action.sign_document')}
                  </span>
                  {isSignSending ? (
                    <></>
                  ) : (
                    <FormOutlined
                      style={{
                        marginLeft: isReductScreen ? 0 : 8
                      }}
                    />
                  )}
                </Button>
              )}
            {(!ticketStatuses.includes(contract?.exemption?.status) ||
              contract?.type === 'RCNGAWK') &&
              !paperworkFeeNotPaid && (
                <Tooltip
                  title={
                    disabledButton()
                      ? 'Actions limitées : la date d’effet a plus de 10 jours et des documents sont non conformes ou manquants dans la GED.'
                      : undefined
                  }
                >
                  <Button
                    onClick={() => {
                      if (contract.status !== 'QUOTE') {
                        setOpenPrintModal(true);
                      } else {
                        printContract('quote');
                      }
                      setAction('print');
                    }}
                    type="primary"
                    style={{
                      width: isReductScreen ? 35 : 'auto',
                      padding: isReductScreen ? '4px 4px' : '4px 15px'
                    }}
                    loading={isDownloading}
                    disabled={contract.status === 'DELETE' || disabledButton()}
                  >
                    <span
                      style={{
                        display: isReductScreen ? 'none' : 'inline'
                      }}
                    >
                      {contract && contract?.previous_contract
                        ? t('contracts.form.title.action.print_amendment')
                        : t(
                            `contracts.form.title.action.print_${
                              contract.status === 'QUOTE' ? 'quote' : 'contract'
                            }`
                          )}
                    </span>
                    {isDownloading ? (
                      <></>
                    ) : (
                      <PrinterOutlined
                        style={{
                          marginLeft: isReductScreen ? 0 : 8
                        }}
                      />
                    )}
                  </Button>
                </Tooltip>
              )}
            <MultiplePrintModal
              openPrintModal={openPrintModal}
              setOpenPrintModal={setOpenPrintModal}
              printContract={printContract}
              sendContract={sendContract}
              action={action}
              form={form}
              contract={contract}
            />
            <MultipleSignModal
              openSignModal={openSignModal}
              setOpenSignModal={setOpenSignModal}
              isSignSending={isSignSending}
              signContract={signContract}
              form={form}
              contract={contract}
            />
            {user?.role !== 'guests:GUEST' && (
              <>
                {!ticketStatuses.includes(contract?.exemption?.status) &&
                  !paperworkFeeNotPaid && (
                    <>
                      <Tooltip
                        title={
                          disabledButton()
                            ? 'Actions limitées : la date d’effet a plus de 10 jours et des documents sont non conformes ou manquants dans la GED.'
                            : undefined
                        }
                      >
                        <Button
                          onClick={() => {
                            if (contract.status !== 'QUOTE') {
                              setOpenPrintModal(true);
                            } else {
                              setOpenTextQuoteModal(true);
                            }
                            setAction('send');
                          }}
                          disabled={
                            !contract?.customer?.email ||
                            contract.status === 'DELETE' ||
                            disabledButton()
                          }
                          loading={downloading}
                          type="primary"
                          style={{
                            width: isReductScreen ? 35 : 'auto',
                            padding: isReductScreen ? '4px 4px' : '4px 15px'
                          }}
                        >
                          <span
                            style={{
                              display: isReductScreen ? 'none' : 'inline'
                            }}
                          >
                            {contract && contract?.previous_contract
                              ? t('contracts.form.title.action.mail_amendment')
                              : t(
                                  `contracts.form.title.action.mail_${
                                    contract.status === 'QUOTE'
                                      ? 'quote'
                                      : 'contract'
                                  }`
                                )}
                          </span>
                          <MailOutlined
                            style={{
                              marginLeft: isReductScreen ? 0 : 8
                            }}
                          />
                        </Button>
                      </Tooltip>
                      <TextQuoteModal
                        openTextQuoteModal={openTextQuoteModal}
                        setOpenTextQuoteModal={setOpenTextQuoteModal}
                        sendContract={sendContract}
                        contract={contract}
                      />
                    </>
                  )}
                {contract.status === 'QUOTE' && (
                  <Tooltip title={t('buttons.update_details')}>
                    <Button
                      type="primary"
                      style={{
                        width: isReductScreen ? 35 : 'auto',
                        padding: isReductScreen ? '4px 4px' : '4px 15px',
                        backgroundColor: '#1677ff'
                      }}
                      disabled={contract?.status === 'DELETE'}
                      onClick={() => setUpdateDetailsVisible(true)}
                      icon={<ContactsOutlined />}
                    >
                      <span
                        style={{ display: isReductScreen ? 'none' : 'inline' }}
                      >
                        {`${t('buttons.update_details')} `}
                      </span>
                    </Button>
                  </Tooltip>
                )}
                {contract.status === 'QUOTE' && (
                  <Button
                    disabled={
                      contract?.exemption &&
                      contract?.exemption?.status !== 'ACCEPTED'
                    }
                    type="primary"
                    style={{
                      width: isReductScreen ? 35 : 'auto',
                      padding: isReductScreen ? '4px 4px' : '4px 15px',
                      backgroundColor: 'green'
                    }}
                    icon={<CheckOutlined />}
                    onClick={() => {
                      handleOpenModal(contract?._id, contract?.type, contract);
                    }}
                  >
                    <span
                      style={{
                        display: isReductScreen ? 'none' : 'inline'
                      }}
                    >
                      {`${t(
                        `contracts.form.title.action.validate_${
                          contract?.previous_contract ? 'amendment' : 'quote'
                        }`
                      )} `}
                    </span>
                  </Button>
                )}
                {!contract.exemption && (
                  <Dropdown dropdownRender={() => menu} placement="bottomRight">
                    <Button
                      type="link"
                      style={{
                        color: 'var(--textColorSecondary)',
                        paddingLeft: 0,
                        paddingRight: 0
                      }}
                    >
                      <EllipsisOutlined
                        style={{
                          fontSize: 16,
                          display: isReductScreen ? 'inline' : 'none'
                        }}
                      />
                      <span
                        style={{
                          display: isReductScreen ? 'none' : 'inline'
                        }}
                      >
                        {`${t('contracts.form.title.action.more_actions')} `}
                        <DownOutlined style={{ marginLeft: 4, fontSize: 13 }} />
                      </span>
                    </Button>
                  </Dropdown>
                )}
              </>
            )}
            {user?.role === 'guests:GUEST' && (
              <Button
                type="primary"
                style={{
                  width: isReductScreen ? 35 : 'auto',
                  padding: isReductScreen ? '4px 4px' : '4px 15px'
                }}
                disabled={contract?.status === 'DELETE'}
                onClick={() => {
                  setUpdateDetailsVisible(true);
                }}
              >
                <span style={{ display: isReductScreen ? 'none' : 'inline' }}>
                  {`${t('buttons.update_details')} `}
                </span>
                <InfoCircleOutlined
                  style={{
                    marginLeft: isReductScreen ? 0 : 8
                  }}
                />
              </Button>
            )}
          </>
        </Space>
      </>
    </Skeleton>
  );
};

Actions.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  setOpenLastStepModal: PropTypes.func.isRequired,
  contract: PropTypes.shape({
    local: PropTypes.bool,
    amendment: PropTypes.arrayOf(PropTypes.string),
    contract_signed: PropTypes.bool,
    effective_date: PropTypes.instanceOf(Date),
    termination_date: PropTypes.instanceOf(Date),
    customer: PropTypes.shape({
      email: PropTypes.string,
      _id: PropTypes.string
    }),
    documents: PropTypes.arrayOf(PropTypes.string),
    status: PropTypes.string,
    quittances: PropTypes.array,
    _id: PropTypes.string,
    termination_reason: PropTypes.string,
    number: PropTypes.string,
    type: PropTypes.string,
    previous_contract: PropTypes.string,
    exemption: PropTypes.shape({
      exemptions: PropTypes.arrayOf(PropTypes.string),
      _id: PropTypes.string,
      status: PropTypes.string
    }),
    activities: PropTypes.arrayOf(PropTypes.string),
    paperwork_fee_invoice: PropTypes.string
  }).isRequired,
  shownContract: PropTypes.shape({ termination_date: PropTypes.string })
    .isRequired,
  openLastStepModal: PropTypes.bool.isRequired
};

export default Actions;
